import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { baseUrl, headersDefault } from "../redux/actions";
import { safeNavigate } from "./navigationHelper";
import { SUBSCRIPTIONS_SHOP_PATH } from "./shopPaths";

/**
 * Redirects the user to the current dynamic Premium landing page.
 *
 * On native (iOS/Android), Apple/Google IAP rules force us through the
 * RevenueCat-backed paywall — Stripe checkout inside the app would get the
 * build rejected. Native users go straight to /paywall regardless of what the
 * pre-register widget would have routed them to.
 *
 * On web the behavior mirrors the burger menu's "Passa a Premium" button:
 *   1. Fetch /api/app/images/pre-register to get the active premium widget
 *   2. Use its first slide's link (internal path or external URL)
 *   3. Fall back to the Shop's subscriptions on any failure
 *
 * A `url` configured in the CRM overrides all of the above, on every platform.
 * That is a deliberate product decision taken on 28 August 2026: the team wants
 * the destination of the red banner and of the "Diventa Premium" popup buttons to
 * be editable without an app release.
 *
 * ⚠️ It carries a store-review risk that is worth restating wherever this is
 * read. Apple Guideline 3.1.1 does not allow an iOS build to send members to an
 * outside payment flow, and /shopping-widget is exactly that — Stripe. Pointing
 * these buttons there on native reintroduces the leak the IAP compliance work
 * removed. Informational destinations are fine; a checkout is not.
 *
 * @param {object} options
 * @param {object} options.history - react-router history instance
 * @param {string} options.pathname - current pathname (for safeNavigate)
 * @param {string} [options.url] - destination configured in the CRM, if any
 */
export const redirectToPremium = async ({ history, pathname, url = null }) => {
  const configured = typeof url === "string" ? url.trim() : "";

  if (configured) {
    // An internal path goes through safeNavigate so the shop widget's own
    // router is torn down properly, the same as every other route change.
    if (configured.startsWith("/")) {
      safeNavigate(history, configured, pathname);
      return;
    }

    if (/^https?:\/\//i.test(configured)) {
      if (Capacitor.getPlatform() === "web") {
        window.location.href = configured;
        return;
      }

      // On a phone the app IS the web view: setting location.href would replace
      // the app shell and leave no way back. The system browser keeps it alive.
      await Browser.open({ url: configured });
      return;
    }

    // Anything else — a relative fragment, a typo — is not something we can
    // open. Fall through to the default resolution rather than going nowhere.
  }

  if (Capacitor.getPlatform() !== "web") {
    safeNavigate(history, "/paywall", pathname);
    return;
  }

  try {
    const response = await fetch(`${baseUrl}/api/app/images/pre-register`, {
      method: "GET",
      headers: headersDefault(),
    });

    if (!response.ok) {
      safeNavigate(history, SUBSCRIPTIONS_SHOP_PATH, pathname);
      return;
    }

    const data = await response.json();
    const widgets = Array.isArray(data?.widgets) ? data.widgets : [];
    const premiumWidget = widgets.find((widget) =>
      widget?.subscription_types?.some(
        (subscription) => subscription?.name === "premium"
      )
    );
    const premiumSlide = Array.isArray(premiumWidget?.slides)
      ? premiumWidget.slides[0]
      : null;
    const targetLink = premiumSlide?.link || SUBSCRIPTIONS_SHOP_PATH;

    if (typeof targetLink === "string" && targetLink.startsWith("/")) {
      safeNavigate(history, targetLink, pathname);
      return;
    }

    if (typeof targetLink === "string" && /^https?:\/\//i.test(targetLink)) {
      window.location.href = targetLink;
      return;
    }

    safeNavigate(history, SUBSCRIPTIONS_SHOP_PATH, pathname);
  } catch {
    safeNavigate(history, SUBSCRIPTIONS_SHOP_PATH, pathname);
  }
};
